import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  ArrowRight, ShieldCheck, FileText, Check, AlertCircle, AlertTriangle, Eye, Lock, Sparkles, TrendingDown,
  Scale, Heart, Gauge, BadgeCheck,
} from "lucide-react";

import { api } from "@/lib/api";
import CheckerCard from "@/components/CheckerCard";
import WhyCheckExpert from "@/components/WhyCheckExpert";

const SAMPLE_FINDINGS = [
  { sev: "critical", color: "#DC2626", bg: "#FEF2F2", title: "Waarborg overschrijdt wettelijk maximum (3 maanden BE)", art: "Vlaams Woninghuurdecreet art. 12" },
  { sev: "high", color: "#EA580C", bg: "#FFF7ED", title: "Opzegclausule vaag — risico op willekeur", art: "BW Boek 5" },
  { sev: "medium", color: "#D97706", bg: "#FFFBEB", title: "Indexeringsformule niet conform actuele wetgeving", art: "Decreet 2022" },
];

const STEPS = [
  { n: "01", title: "Kies een check", text: "Selecteer het type document. We hebben er 19, elk met eigen juridisch kader." },
  { n: "02", title: "Upload & betaal", text: "PDF, Word, foto of tekst. Versleuteld geüpload via MultiSafepay." },
  { n: "03", title: "Ontvang rapport", text: "Score 0–100, bevindingen per ernstklasse, concrete actiestappen — in &lt; 60 sec." },
];

const VALUES = [
  { Icon: Scale, title: "Onafhankelijk", text: "Geen banden met aanbieders, banken, verzekeraars of fabrikanten. Geen affiliate-deals, geen commissies." },
  { Icon: Heart, title: "Voor particulieren", text: "We bedienen huurders, kopers, werknemers, patiënten en gezinnen — niet de grote bedrijven." },
  { Icon: BadgeCheck, title: "Specialistische expertise", text: "Voor elk documenttype bouwden we een eigen kader, getoetst aan BE/NL-wetgeving en marktstandaarden." },
];

export default function Home() {
  const [checkers, setCheckers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/checkers").then((r) => setCheckers(r.data.checkers || [])).catch(() => {});
  }, []);

  const checkersByCategory = useMemo(() => {
    const groups = {};
    for (const c of checkers) {
      const key = c.category || "overig";
      if (!groups[key]) groups[key] = [];
      groups[key].push(c);
    }
    return groups;
  }, [checkers]);

  function scrollToChecks(e) {
    e?.preventDefault?.();
    const el = document.getElementById("alle-checks");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/checks");
    }
  }

  return (
    <div className="min-h-screen bg-background font-modern" data-testid="home">
      <Seo path="/" />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/40 pt-14 sm:pt-20 pb-12">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: "radial-gradient(#002FA7 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100/70 backdrop-blur text-[#002FA7] text-[11px] font-semibold uppercase tracking-[0.18em]">
              <Sparkles className="h-3 w-3" /> Voor particulieren · BE &amp; NL
            </div>
            <h1
              className="font-editorial text-[3rem] sm:text-[4.4rem] lg:text-[5.2rem] font-medium text-ink mt-6 leading-[1.02]"
              style={{ fontVariationSettings: "'opsz' 144", letterSpacing: "-0.025em" }}
            >
              Wat staat er <span className="text-[#002FA7]">echt</span> in je document?
            </h1>
            <p className="font-modern text-lg sm:text-xl text-ink-soft mt-7 max-w-xl leading-[1.55] tracking-[-0.005em]">
              Huurcontract, polis, loonstrook of energiefactuur? Wij lezen het door, vergelijken met de actuele BE/NL wetgeving en zeggen je <b className="text-ink font-semibold">in 60 seconden</b> waar de risico's zitten.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={scrollToChecks}
                className="font-modern inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold text-base shadow-lg shadow-blue-600/20 transition-all hover:shadow-xl hover:shadow-blue-600/30"
                data-testid="hero-cta"
              >
                Start je analyse — €4,99 <ArrowRight className="h-4 w-4" />
              </button>
              <Link to="/hoe-het-werkt" className="font-modern text-sm font-semibold text-ink-soft hover:text-ink underline-offset-4 hover:underline" data-testid="hero-secondary">
                Hoe werkt het?
              </Link>
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-ink-soft font-modern">
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" /> Geen account vereist</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" /> GDPR-conform</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600" /> Resultaat in &lt; 60 sec</span>
            </div>
          </div>

          {/* Report preview mock */}
          <div className="relative" data-testid="hero-preview">
            <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-tr from-[#002FA7]/15 to-[#00B4D8]/15 rounded-full blur-3xl" />
            <div className="absolute -bottom-6 -left-6 w-64 h-64 bg-gradient-to-tr from-[#10B981]/10 to-[#0EA5E9]/10 rounded-full blur-3xl" />

            <div className="relative bg-white rounded-3xl shadow-2xl ring-1 ring-slate-200/60 overflow-hidden font-modern">
              <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-100 bg-slate-50/60">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="h-8 w-8 rounded-lg bg-[#002FA7] flex items-center justify-center text-white flex-shrink-0">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink truncate">huurcontract-gent-2026.pdf</div>
                    <div className="text-xs text-ink-soft">5 pagina's · 224 KB</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                  <Check className="h-3 w-3" /> Geanalyseerd
                </span>
              </div>

              <div className="px-6 py-6 grid grid-cols-[auto_1fr] gap-5 items-center bg-gradient-to-br from-white to-blue-50/40">
                <div className="flex flex-col items-center">
                  <span className="text-[10px] uppercase tracking-wider text-ink-soft font-bold">Score</span>
                  <div className="font-editorial text-6xl font-medium text-[#002FA7] leading-none mt-1" style={{ letterSpacing: "-0.04em" }}>72</div>
                  <span className="text-xs text-ink-soft mt-1">van 100</span>
                </div>
                <div className="flex-1">
                  <div className="text-xs text-ink-soft uppercase tracking-wider font-bold mb-1.5">Resultaat</div>
                  <div className="text-base font-semibold text-ink">Acceptabel, met 3 aandachtspunten</div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <div className="rounded-lg bg-red-50 px-2 py-1.5 text-center"><div className="text-base font-bold text-red-700">1</div><div className="text-[10px] text-red-700 font-semibold">KRITIEK</div></div>
                    <div className="rounded-lg bg-orange-50 px-2 py-1.5 text-center"><div className="text-base font-bold text-orange-700">1</div><div className="text-[10px] text-orange-700 font-semibold">HOOG</div></div>
                    <div className="rounded-lg bg-amber-50 px-2 py-1.5 text-center"><div className="text-base font-bold text-amber-700">1</div><div className="text-[10px] text-amber-700 font-semibold">AANDACHT</div></div>
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 divide-y divide-slate-100">
                <div className="px-6 py-2 bg-slate-50/60">
                  <span className="text-[10px] uppercase tracking-wider text-ink-soft font-bold">19 controles · 3 aandachtspunten</span>
                </div>
                {SAMPLE_FINDINGS.map((f, i) => (
                  <div key={i} className="flex items-start gap-3 px-6 py-3.5">
                    <div className="h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: f.bg }}>
                      {f.sev === "critical" ? <AlertCircle className="h-4 w-4" style={{ color: f.color }} /> : <AlertTriangle className="h-4 w-4" style={{ color: f.color }} />}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink leading-snug">{f.title}</div>
                      <div className="text-[11px] tracking-wide font-medium mt-0.5 text-ink-soft">{f.art}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-slate-100 px-6 py-3 flex items-center justify-between bg-slate-50/40">
                <div className="text-xs text-ink-soft inline-flex items-center gap-1.5"><Lock className="h-3 w-3" /> 256-bit beveiligd · 30 dagen toegang</div>
                <div className="text-xs font-semibold text-ink">PDF · share-link</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      <section className="bg-ink text-white py-14">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-10 font-modern">
          {[
            { value: "25", label: "documenttypes gedekt" },
            { value: "29", label: "BE/NL wettelijke kaders" },
            { value: "< 60s", label: "tot je rapport beschikbaar is" },
            { value: "€4,99", label: "per analyse · eenmalig" },
          ].map((s, i) => (
            <div key={i} className="text-center sm:text-left" data-testid={`stat-${i}`}>
              <div className="font-editorial text-4xl sm:text-5xl font-medium text-white" style={{ letterSpacing: "-0.025em" }}>{s.value}</div>
              <div className="text-xs sm:text-sm text-slate-400 mt-1.5">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY CHECKEXPERT VS AI — conversie-blok */}
      <WhyCheckExpert ctaHref="/checks" className="bg-gradient-to-br from-slate-50/50 via-white to-blue-50/30 border-y border-blue-100/50" />

      {/* AllesCheck banner — low-threshold instap */}
      <section className="py-12 bg-gradient-to-br from-blue-50/60 to-white border-y border-blue-100/60" data-testid="allescheck-banner">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-2xl bg-white border border-[#002FA7]/20 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 shadow-sm">
            <div className="h-14 w-14 rounded-2xl bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <h2 className="font-editorial text-2xl sm:text-3xl font-medium text-ink" style={{ letterSpacing: "-0.02em" }}>
                Geen idee welke check je nodig hebt?
              </h2>
              <p className="text-ink-soft mt-2 leading-relaxed">
                Upload gewoon je document. <b className="text-ink">Alles Check</b> herkent automatisch het documenttype en voert de juiste analyse uit.
              </p>
            </div>
            <Link
              to="/checks/allescheck"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold shadow-sm whitespace-nowrap"
              data-testid="allescheck-cta"
            >
              Probeer Alles Check <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ALLE 19 CHECKERS — per categorie gescheiden */}
      <section className="py-20" id="alle-checks">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_auto] items-end gap-6">
            <div>
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#002FA7] font-modern">Welke documenten?</span>
              <h2 className="font-editorial text-4xl sm:text-5xl font-medium text-ink mt-3 leading-[1.05]" style={{ letterSpacing: "-0.025em" }}>
                25 specialistische checks — <span className="text-[#002FA7]">kies wat je nodig hebt</span>
              </h2>
              <p className="text-ink-soft mt-4 max-w-3xl font-modern leading-relaxed">
                Elke check heeft zijn eigen kader, getoetst aan BE/NL-wetgeving en marktstandaarden. Geen generieke samenvatting — een analyse op maat van het documenttype.
              </p>
            </div>
            <Link to="/checks" className="hidden lg:inline-flex items-center gap-1.5 text-sm font-semibold text-[#002FA7] hover:underline underline-offset-4 font-modern">
              Bekijk de hub <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {checkers.length > 0 && (
            <div className="mt-12 space-y-12" data-testid="checkers-grouped">
              {Object.entries(checkersByCategory).map(([catSlug, items]) => (
                <section key={catSlug} className="" data-testid={`home-cat-${catSlug}`}>
                  <div className="flex items-baseline gap-3 mb-4 pb-3 border-b border-border">
                    <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: items[0]?.category_color || "#002FA7" }} aria-hidden />
                    <h3 className="font-editorial text-xl sm:text-2xl font-semibold text-ink" style={{ letterSpacing: "-0.02em" }}>
                      {items[0]?.category_label || catSlug}
                    </h3>
                    <span className="text-xs text-ink-subtle font-modern">{items.length} {items.length === 1 ? "check" : "checks"}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((c) => (
                      <CheckerCard key={c.slug} checker={c} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-slate-50/60">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#002FA7] font-modern">Zo werkt het</span>
            <h2 className="font-editorial text-4xl sm:text-5xl font-medium text-ink mt-3 leading-[1.05]" style={{ letterSpacing: "-0.025em" }}>
              Van document naar zekerheid in drie stappen
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4 font-modern">
            {STEPS.map((s) => (
              <div key={s.n} className="relative rounded-2xl bg-white border border-border p-7">
                <span className="absolute -top-4 left-7 font-editorial text-5xl font-semibold text-[#002FA7]/15 leading-none" style={{ letterSpacing: "-0.04em" }}>{s.n}</span>
                <h3 className="font-editorial text-xl font-semibold text-ink mt-3">{s.title}</h3>
                <p className="text-sm text-ink-soft mt-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: s.text }} />
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/hoe-het-werkt" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#002FA7] hover:underline underline-offset-4 font-modern">
              Lees de volledige uitleg <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* VALUES / WHY */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#002FA7] font-modern">Waarom CheckExpert</span>
            <h2 className="font-editorial text-4xl sm:text-5xl font-medium text-ink mt-3 leading-[1.05]" style={{ letterSpacing: "-0.025em" }}>
              Eenvoudig uitgelegd.<br />Jouw zekerheid.
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 font-modern">
            {VALUES.map((v) => (
              <div key={v.title} className="rounded-2xl bg-white border border-border p-7">
                <div className="h-11 w-11 rounded-xl bg-[#002FA7] text-white flex items-center justify-center">
                  <v.Icon className="h-5 w-5" />
                </div>
                <h3 className="font-editorial text-xl font-semibold text-ink mt-4">{v.title}</h3>
                <p className="text-sm text-ink-soft mt-2 leading-relaxed">{v.text}</p>
              </div>
            ))}
            <div className="rounded-2xl border border-border bg-gradient-to-br from-blue-50 to-white p-7">
              <div className="h-11 w-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
                <TrendingDown className="h-5 w-5" />
              </div>
              <h3 className="font-editorial text-xl font-semibold text-ink mt-4">Betaal nooit te veel</h3>
              <p className="text-sm text-ink-soft mt-2 leading-relaxed">Verborgen kosten, onnodige clausules en marktbenchmark — we wijzen ze gewoon aan.</p>
            </div>
            <div className="rounded-2xl border border-border bg-white p-7">
              <div className="h-11 w-11 rounded-xl bg-amber-500 text-white flex items-center justify-center">
                <Eye className="h-5 w-5" />
              </div>
              <h3 className="font-editorial text-xl font-semibold text-ink mt-4">Verborgen valkuilen zichtbaar</h3>
              <p className="text-sm text-ink-soft mt-2 leading-relaxed">Onrechtmatige clausules en risico's worden expliciet gemarkeerd met severity-label.</p>
            </div>
            <div className="rounded-2xl border border-border bg-white p-7">
              <div className="h-11 w-11 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                <Gauge className="h-5 w-5" />
              </div>
              <h3 className="font-editorial text-xl font-semibold text-ink mt-4">60 seconden tot rapport</h3>
              <p className="text-sm text-ink-soft mt-2 leading-relaxed">Geen wachttijd, geen afspraak. Upload, betaal €4,99, ontvang je rapport.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#002FA7] to-[#001a5c] text-white p-10 sm:p-14 text-center shadow-2xl">
            <div aria-hidden className="pointer-events-none absolute -top-12 -right-12 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
            <div aria-hidden className="pointer-events-none absolute -bottom-16 -left-12 h-56 w-56 rounded-full bg-[#00B4D8]/20 blur-3xl" />
            <ShieldCheck className="relative h-9 w-9 mx-auto text-blue-200" />
            <h2 className="relative font-editorial text-4xl sm:text-5xl font-medium mt-5" style={{ letterSpacing: "-0.025em" }}>
              €4,99 nu, <span className="text-blue-200">honderden euro's</span> bespaard.
            </h2>
            <p className="relative text-blue-100 mt-5 max-w-xl mx-auto font-modern leading-relaxed">
              Upload je document. Wij geven je in 60 seconden een eerlijk, helder rapport — met de exacte wetgeving erbij.
            </p>
            <button
              type="button"
              onClick={scrollToChecks}
              className="relative mt-9 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-ink font-semibold hover:bg-blue-50 transition-colors font-modern"
              data-testid="bottom-cta"
            >
              Start je analyse <ArrowRight className="h-4 w-4" />
            </button>
            <div className="relative mt-7 text-xs text-blue-200 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-modern">
              <span>✓ Bancontact · iDEAL · Wero · Visa · Mastercard</span>
              <span>✓ GDPR-conform</span>
              <span>✓ Geen account vereist</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
